import { useLayoutEffect } from 'react';

const SIBLINGS_SELECTOR = 'body > *:not(script):not(style)';

/**
 * Marks background page content `inert` while a modal dialog is open, so iOS
 * Safari + VoiceOver's left/right swipe (flick) gesture cannot escape the
 * dialog into the content behind it (WCAG 1.3.1 / 2.4.3).
 *
 * Radix already applies `aria-hidden` to background peers via `hideOthers`, but
 * `aria-hidden` alone is not reliably honored by VoiceOver's swipe gesture on
 * iOS — `inert` is the load-bearing addition here. This mirrors the legacy
 * `ssStackItems.BlockSiblings` handler in webroot/js/stack_items.js, which
 * fixes the same issue for the jQuery/Bootstrap lightbox modals.
 *
 * Only elements this hook makes inert are restored on cleanup; body children
 * that were already inert for another reason are left untouched.
 *
 * @param active   Whether the dialog is open.
 * @param dialogEl The dialog content element. The body-level branch that hosts
 *                 this element (the portal and its ancestors) is left
 *                 interactive. `null` means it is not mounted yet — skip.
 */
export const useInertBackground = (active: boolean, dialogEl: HTMLElement | null): void => {
    useLayoutEffect(() => {
        if (!active || !dialogEl) return;

        const inerted: HTMLElement[] = [];
        document.querySelectorAll<HTMLElement>(SIBLINGS_SELECTOR).forEach((el) => {
            // Leave the branch that hosts the dialog (portal + its ancestors) interactive.
            if (el.contains(dialogEl)) return;
            // Radix portals its overlay and content as separate direct <body> children,
            // both tagged with `data-state`. Inerting the overlay would suppress the
            // pointer events Radix's DismissableLayer needs to close on backdrop tap, so
            // skip any Radix-portaled layer here.
            if (el.hasAttribute('data-state')) return;
            // Preserve inert that pre-existed for another reason (e.g. a closed modal).
            if (el.hasAttribute('inert')) return;
            el.setAttribute('inert', '');
            inerted.push(el);
        });

        return () => {
            inerted.forEach((el) => el.removeAttribute('inert'));
        };
    }, [active, dialogEl]);
};
